import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, UserRound, LogOut, Star, Folder, PanelLeftClose, PanelLeft } from 'lucide-react'
import { useAuth, useWorkspaceOrgId } from '../lib/auth'
import { isPlatformAdmin } from '../lib/roles'
import { recentOrgs } from '../lib/recentOrgs'
import { listAreas } from '../lib/processIntelligence'
import { SHELL_MODULES, isSoon, moduleForPath, phaseVisible, shellLabel, soonVisible, type PanelItem, type ShellModule } from '../lib/navigation'
import { can } from '../lib/permissions'
import type { ViewAsState } from '../lib/useViewAs'
import type { PiArea } from '../types/processIntelligence'
import OrgBadge from './ui/OrgBadge'
import Avatar from './ui/Avatar'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover'
import { LogoMark } from './Logo'
import { cn } from '../lib/utils'

// Live alert dots keyed by module badgeKey (ENG-30).
export type NavBadges = Partial<Record<string, boolean>>

// Variant C navigation: module bar + a panel that follows the module (quick views,
// favourites, area tree). Items follow the role from useViewAs: the real one, or a
// consultant's "View as" preview, chosen in the client switcher; it changes the navigation, never the API.
export default function Sidebar({
  collapsed,
  canCollapse = true,
  onToggleCollapse,
  badges = {},
  orgName,
  enabledModules = null,
  onOpenSwitcher,
  bell,
  viewAs,
}: {
  collapsed: boolean
  canCollapse?: boolean
  onToggleCollapse: () => void
  badges?: NavBadges
  orgName?: string | null
  enabledModules?: string[] | null
  onOpenSwitcher: () => void
  bell?: ReactNode
  viewAs: ViewAsState
}) {
  const { me, logout } = useAuth()
  const orgId = useWorkspaceOrgId()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { t } = useTranslation('admin')

  const isAdmin = isPlatformAdmin(me)
  const { role } = viewAs

  const zone: 'platform' | 'client' = orgId ? 'client' : 'platform'
  const piOn = !!enabledModules?.includes('process_intelligence')
  const [areas, setAreas] = useState<PiArea[]>([])
  useEffect(() => {
    if (!orgId || !piOn) return
    let cancelled = false
    listAreas(orgId).then((r) => { if (!cancelled) setAreas(r.areas) }, () => {})
    return () => { cancelled = true }
  }, [orgId, piOn])

  // enabledModules is null while it loads; treat that as on rather than flicker items away.
  const moduleOff = (i: PanelItem) => !!i.requiresModule && !!enabledModules && !enabledModules.includes(i.requiresModule)
  const itemVisible = (m: ShellModule) => (i: PanelItem) =>
    (!i.platformAdminOnly || isAdmin) &&
    (!i.permission || can(role, i.permission)) &&
    phaseVisible(role, i, m.key) &&
    soonVisible(i) &&
    (!moduleOff(i) || role === 'consultant')

  const modules = SHELL_MODULES.filter((m) =>
    m.zone === zone && (!m.platformAdminOnly || isAdmin) && m.groups.some((g) => g.items.some(itemVisible(m))))
  // A page outside every module (profile) shows the first panel but lights up nothing.
  const matched = moduleForPath(pathname, zone)
  const active = matched ?? modules[0]

  const hrefFor = (i: PanelItem): string => {
    const path = i.to ? (i.orgInPath && orgId ? `${i.to}/${orgId}` : i.to) : `/soon/${i.key}`
    const params = new URLSearchParams(i.query)
    if (zone === 'client' && orgId && !i.orgInPath) params.set('org_id', orgId)
    const qs = params.toString()
    return qs ? `${path}?${qs}` : path
  }
  const isCurrent = (i: PanelItem): boolean => {
    const target = new URL(hrefFor(i), 'http://x')
    const below = !i.query && !i.orgInPath && pathname.startsWith(`${target.pathname}/`)
    if (target.pathname !== pathname && !below) return false
    const now = new URLSearchParams(search)
    const want = new URLSearchParams(i.query)
    for (const k of ['status', 'area', 'tab']) if ((now.get(k) ?? '') !== (want.get(k) ?? '')) return false
    return true
  }
  const landing = (m: ShellModule) => {
    const first = m.groups.flatMap((g) => g.items).find(itemVisible(m))
    return first ? hrefFor(first) : '/home'
  }

  async function handleSignOut() {
    if (await logout()) navigate('/login', { replace: true })
  }

  // A module whose visible items are all unbuilt shows muted in the bar.
  const allSoon = (m: ShellModule) => m.groups.flatMap((g) => g.items).filter(itemVisible(m)).every(isSoon)

  const barButton = (m: ShellModule) => {
    const on = m.key === matched?.key
    const soon = allSoon(m)
    const Icon = m.icon
    const label = shellLabel('modules', m.key)
    return (
      <Link
        key={m.key}
        to={landing(m)}
        title={soon ? `${label} · ${t('shellNav.panel.soon')}` : label}
        className="group flex flex-col items-center gap-1 py-1"
      >
        <span className={cn(
          'relative grid size-10 place-items-center rounded-xl transition-colors',
          on ? 'bg-card text-foreground shadow-[var(--shadow-card)]' : 'group-hover:bg-frost/50 group-hover:text-foreground',
          !on && (soon ? 'text-muted-foreground/70' : 'text-foreground/70'),
        )}>
          <Icon className="size-[18px]" />
          {m.badgeKey && badges[m.badgeKey] && can(role, 'interview.configure') && <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-warning" />}
        </span>
        <span className={cn(
          'max-w-[84px] truncate text-caption leading-none',
          on ? 'font-medium text-foreground' : soon ? 'text-muted-foreground/80' : 'text-foreground/70',
        )}>
          {label}
        </span>
      </Link>
    )
  }

  const bar = (
    <div className="flex w-[88px] shrink-0 flex-col items-center py-3">
      <Link to="/home" title="Claritio" className="mb-3 grid size-10 place-items-center">
        <LogoMark className="size-7" />
      </Link>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {modules.filter((m) => m.place !== 'bottom').map(barButton)}
      </nav>
      <div className="flex flex-col items-center gap-1 pt-2">
        {bell && <div className="grid size-10 place-items-center">{bell}</div>}
        {modules.filter((m) => m.place === 'bottom').map(barButton)}
        <Popover>
          <PopoverTrigger asChild>
            <button title={me?.user?.name || me?.user?.email || ''} className="mt-1 rounded-full ring-2 ring-frost/70">
              <Avatar name={me?.user?.name || me?.user?.email} size={32} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="right" className="w-52">
            <div className="px-2 py-1.5 text-xs text-muted-foreground">{me?.user?.email}</div>
            <Link to={orgId ? `/profile?org_id=${orgId}` : '/profile'} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
              <UserRound className="size-3.5" /> {t('shellNav.myProfile')}
            </Link>
            <button onClick={handleSignOut} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
              <LogOut className="size-3.5" /> {t('shell.signOut')}
            </button>
          </PopoverContent>
        </Popover>
        {canCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? t('shell.expand') : t('shell.collapse')}
            className="mt-1 grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-frost/50 hover:text-foreground"
          >
            {collapsed ? <PanelLeft className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        )}
      </div>
    </div>
  )

  const row = (i: PanelItem) => {
    const soon = isSoon(i)
    return (
      <Link
        key={`${i.key}-${i.query ?? ''}`}
        to={hrefFor(i)}
        className={cn(
          'mb-0.5 flex items-center gap-2 rounded-xl px-2.5 py-[7px] text-sm transition-colors',
          isCurrent(i) ? 'bg-card font-medium text-foreground shadow-[var(--shadow-card)]' : 'text-foreground/80 hover:bg-frost/50 hover:text-foreground',
        )}
      >
        <span className="flex-1 truncate">{shellLabel('items', i.key)}</span>
        {moduleOff(i) ? (
          <span className="rounded-full border border-frost/80 px-1.5 py-px text-caption text-muted-foreground">{t('shellNav.panel.moduleOff')}</span>
        ) : soon && <span className="rounded-full border border-frost/80 bg-frost/40 px-1.5 py-px text-caption text-muted-foreground">{t('shellNav.panel.soon')}</span>}
      </Link>
    )
  }

  const heading = (key: string) => (
    <div className="px-2.5 pb-1 pt-3 text-xs text-muted-foreground">{shellLabel('groups', key)}</div>
  )

  const areaTree = active?.areaTree && zone === 'client' && (
    <>
      {heading('favourites')}
      <div className="flex items-start gap-2 px-2.5 py-1 text-xs text-muted-foreground">
        <Star className="mt-px size-3.5 shrink-0" />
        <span>{t('shellNav.panel.noFavourites')}</span>
      </div>
      {heading('areas')}
      {areas.length === 0 && <div className="px-2.5 py-1 text-xs text-muted-foreground">{t('shellNav.panel.noAreas')}</div>}
      {areas.map((a) => {
        const current = pathname === '/processes' && new URLSearchParams(search).get('area') === String(a.id)
        const content = (
          <>
            <Folder className="size-4 shrink-0" />
            <span className="flex-1 truncate">{a.name}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{a.process_count}</span>
          </>
        )
        return (
          <Link
            key={a.id}
            to={`/processes?org_id=${orgId}&area=${a.id}`}
            className={cn(
              'mb-0.5 flex items-center gap-2 rounded-xl px-2.5 py-[7px] text-sm transition-colors',
              current ? 'bg-card font-medium text-foreground shadow-[var(--shadow-card)]' : 'text-foreground/80 hover:bg-frost/50 hover:text-foreground',
            )}
          >
            {content}
          </Link>
        )
      })}
    </>
  )

  const recents = zone === 'platform' ? recentOrgs() : []
  const visibleGroups = active
    ? active.groups.map((g) => ({ g, items: g.items.filter(itemVisible(active)) })).filter(({ items }) => items.length > 0)
    : []

  const panel = (
    <div className="flex min-w-0 flex-1 flex-col border-l border-frost/60 py-3 pr-2.5 pl-2">
      <button
        onClick={onOpenSwitcher}
        className="flex w-full items-center gap-2.5 rounded-xl bg-frost/70 px-2.5 py-2 text-left shadow-[var(--shadow-card)] ring-1 ring-frost transition-colors hover:bg-frost"
      >
        {zone === 'client' ? <OrgBadge name={orgName ?? '?'} size={24} /> : <span className="size-6 rounded-full bg-[var(--mist)]" />}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">
            {zone === 'client' ? (orgName ?? '…') : t('shellNav.panel.chooseClient')}
          </span>
          {zone === 'client' && viewAs.canPreview && (
            <span className="block truncate text-caption text-muted-foreground">
              {t('shellNav.viewAs.as', { role: t(`shellNav.viewAs.${role}`) })}
            </span>
          )}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
      </button>

      <div className="px-2.5 pb-1 pt-4 font-heading text-lg font-light tracking-tight">
        {active ? shellLabel('modules', active.key) : ''}
      </div>

      <div className="flex-1 overflow-y-auto">
        {visibleGroups.map(({ g, items }) => (
          <div key={g.key}>
            {visibleGroups.length > 1 && heading(g.key)}
            {items.map(row)}
          </div>
        ))}
        {areaTree}
        {recents.length > 0 && (
          <>
            {heading('recentClients')}
            {recents.map((o) => (
              <Link key={o.id} to={`/chat?org_id=${o.id}`} className="mb-0.5 flex items-center gap-2 rounded-xl px-2.5 py-[7px] text-sm text-foreground/80 hover:bg-frost/50 hover:text-foreground">
                <OrgBadge name={o.name} size={20} />
                <span className="truncate">{o.name}</span>
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex h-full">
      {bar}
      {!collapsed && panel}
    </div>
  )
}
